import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, RotateCcw, Wallet } from "lucide-react";
import { supabase } from "../../../Services/supabaseClient";
import { useCompany } from "../../../context/CompanyContext";
import "./fixedAssets.css";
import PostDepreciationModal from "./PostDepreciationModal";
import ReverseDepreciationRunModal from "./ReverseDepreciationRunModal";

export default function FixedAssetDepreciationRuns() {
  const { companyId } = useCompany();
  const nav = useNavigate();

  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showPost, setShowPost] = useState(false);
  const [reverseRun, setReverseRun] = useState(null);

  async function fetchRuns() {
    if (!companyId) return;
    setLoading(true);

    const { data, error } = await supabase
      .from("fixed_asset_depreciation_runs")
      .select(
        "id, company_id, period_month, status, posted_at, posted_by, reversed_at, reversed_by, reversal_notes"
      )
      .eq("company_id", companyId)
      .order("period_month", { ascending: false });

    if (!error) setRuns(data || []);
    setLoading(false);
  }

  useEffect(() => {
    fetchRuns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const postedCount = useMemo(
    () => runs.filter((r) => r.status === "POSTED").length,
    [runs]
  );
  const reversedCount = useMemo(
    () => runs.filter((r) => r.status === "REVERSED").length,
    [runs]
  );

  return (
    <div className="fa-wrap">
      <div className="fa-header">
        <div className="fa-title">
          <h2>Depreciation Runs</h2>
          <p>
            Track monthly depreciation postings and reversals. Reversals create
            journal entries (no deletes).
          </p>
        </div>

        <div className="fa-actions">
          <button className="fa-btn" onClick={() => nav(-1)}>
            <ArrowLeft size={16} /> Back
          </button>
          <button className="fa-btn" onClick={() => setShowPost(true)}>
            <Wallet size={16} /> Post Depreciation
          </button>
        </div>
      </div>

      <div className="fa-grid">
        <div className="fa-card">
          <div className="label">Total Runs</div>
          <div className="value">{runs.length}</div>
          <div className="hint">All-time</div>
        </div>
        <div className="fa-card">
          <div className="label">Posted</div>
          <div className="value">{postedCount}</div>
          <div className="hint">Active postings</div>
        </div>
        <div className="fa-card">
          <div className="label">Reversed</div>
          <div className="value">{reversedCount}</div>
          <div className="hint">Audited reversals</div>
        </div>
        <div className="fa-card">
          <div className="label">Tip</div>
          <div className="value" style={{ fontSize: 14, fontWeight: 800 }}>
            Reverse wrong month
          </div>
          <div className="hint">Then post the correct period</div>
        </div>
      </div>

      <div className="fa-panel">
        {loading ? (
          <div style={{ opacity: 0.8, padding: 10 }}>Loading…</div>
        ) : (
          <table className="fa-table">
            <thead>
              <tr>
                <th>Period</th>
                <th>Status</th>
                <th>Posted at</th>
                <th>Reversed at</th>
                <th style={{ width: 180 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {runs.map((r) => (
                <tr key={r.id}>
                  <td>{String(r.period_month).slice(0, 7)}</td>
                  <td>
                    <span
                      className={`fa-badge ${
                        r.status === "POSTED" ? "fa-badge-active" : ""
                      }`}
                    >
                      {r.status}
                    </span>
                  </td>
                  <td>
                    {r.posted_at ? new Date(r.posted_at).toLocaleString() : "-"}
                  </td>
                  <td>
                    {r.reversed_at
                      ? new Date(r.reversed_at).toLocaleString()
                      : "-"}
                  </td>
                  <td>
                    <div className="fa-row-actions">
                      {r.status === "POSTED" ? (
                        <button
                          className="fa-btn"
                          onClick={() => setReverseRun(r)}
                        >
                          <RotateCcw size={16} /> Reverse
                        </button>
                      ) : (
                        <span style={{ opacity: 0.65, fontSize: 13 }}>—</span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {runs.length === 0 && (
                <tr>
                  <td colSpan={5} style={{ opacity: 0.75 }}>
                    No depreciation runs yet. Post your first month from the
                    button above.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {showPost && (
        <PostDepreciationModal
          companyId={companyId}
          onClose={() => setShowPost(false)}
          onPosted={() => {
            setShowPost(false);
            fetchRuns();
          }}
        />
      )}

      {reverseRun && (
        <ReverseDepreciationRunModal
          run={reverseRun}
          onClose={() => setReverseRun(null)}
          onReversed={() => {
            setReverseRun(null);
            fetchRuns();
          }}
        />
      )}
    </div>
  );
}
